import type { Link, Parent, PhrasingContent, Root, RootContent, Text } from "mdast";

/** 記事へのリンクの href。アプリの URL として解決されても画面を移らないよう、フラグメントにする。 */
const HREF_PREFIX = "#knowledge-article=";

/** backend の KnowledgeArticleLinks と同じ書式 ([[記事名]] / [[記事名|表示名]])。 */
const WIKI_LINK_PATTERN = /\[\[([^[\]|]+)(?:\|([^[\]]*))?\]\]/g;

export function knowledgeArticleNameFromHref(href: string | undefined): string | null {
	return href?.startsWith(HREF_PREFIX) ? decodeURIComponent(href.slice(HREF_PREFIX.length)) : null;
}

/**
 * 記事本文の WikiLink を、記事を開くリンクに変える remark プラグイン。コードやリンクの中はそのまま残す。
 * Markdown の解析で [ ] の前後に分かれた文字列ノードは、つなげてから探す。
 */
export function remarkKnowledgeWikiLinks() {
	return (tree: Root) => {
		transformChildren(tree);
	};
}

function transformChildren(parent: Parent): void {
	const merged: RootContent[] = [];
	for (const child of parent.children) {
		const previous = merged.at(-1);
		if (child.type === "text" && previous?.type === "text") {
			previous.value += child.value;
		} else {
			merged.push(child.type === "text" ? { ...child } : child);
		}
	}

	parent.children = merged.flatMap((child): RootContent[] => {
		if (child.type === "text") return splitWikiLinks(child);
		if (child.type !== "link" && child.type !== "linkReference" && "children" in child) {
			transformChildren(child);
		}
		return [child];
	}) as Parent["children"];
}

function splitWikiLinks(node: Text): PhrasingContent[] {
	const result: PhrasingContent[] = [];
	let last = 0;
	for (const match of node.value.matchAll(WIKI_LINK_PATTERN)) {
		const name = match[1].trim();
		if (name.length === 0) continue;
		if (match.index > last)
			result.push({ type: "text", value: node.value.slice(last, match.index) });
		const link: Link = {
			type: "link",
			url: HREF_PREFIX + encodeURIComponent(name),
			children: [{ type: "text", value: match[2]?.trim() || name }],
		};
		result.push(link);
		last = match.index + match[0].length;
	}
	if (last === 0) return [node];
	if (last < node.value.length) result.push({ type: "text", value: node.value.slice(last) });
	return result;
}
